import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, fmtINR, errMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlus,
  Copy,
  Trash2,
  Pencil,
  Search,
  BadgeCheck,
  CheckCircle2,
  Loader2,
  Users,
  Percent,
  ShieldCheck,
} from "lucide-react";

const emptyForm = { name: "", username: "", email: "", mobile: "", commission_rate: 20, password: "" };

export default function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null); // {username, password}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [payoutTarget, setPayoutTarget] = useState(null);

  const load = () => api.get("/admin/agents").then((r) => setAgents(r.data));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.agent_name,
      username: a.agent_username,
      email: a.email || "",
      mobile: a.mobile || "",
      commission_rate: a.commission_rate,
      password: "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          commission_rate: Number(form.commission_rate),
        };
        if (form.email && form.email.trim()) payload.email = form.email.trim();
        if (form.mobile && form.mobile.trim()) payload.mobile = form.mobile.trim();
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/agents/${editing.agent_id}`, payload);
        toast.success("Agent updated");
      } else {
        const payload = {
          name: form.name,
          username: form.username,
          commission_rate: Number(form.commission_rate),
        };
        if (form.email && form.email.trim()) payload.email = form.email.trim();
        if (form.mobile && form.mobile.trim()) payload.mobile = form.mobile.trim();
        if (form.password) payload.password = form.password;
        const { data } = await api.post("/admin/agents", payload);
        setCreatedPassword({ username: data.user.agent_username, password: data.initial_password });
        toast.success("Agent created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(errMessage(err, "Could not save agent"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/admin/agents/${a.agent_id}`, { active: !a.active });
      toast.success(a.active ? "Agent deactivated" : "Agent reactivated");
      load();
    } catch (err) {
      toast.error(errMessage(err, "Could not update status"));
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/agents/${deleteTarget.agent_id}`);
      toast.success("Agent removed");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMessage(err, "Could not delete agent"));
    }
  };

  const markPaid = async () => {
    if (!payoutTarget) return;
    try {
      const { data } = await api.post(`/admin/agents/${payoutTarget.agent_id}/mark-paid`);
      toast.success(`Marked ${data.updated} commissions as paid`);
      setPayoutTarget(null);
      load();
    } catch (err) {
      toast.error(errMessage(err, "Could not mark paid"));
    }
  };

  const filtered = agents.filter(
    (a) =>
      a.agent_name.toLowerCase().includes(q.toLowerCase()) ||
      a.agent_username.toLowerCase().includes(q.toLowerCase())
  );

  const totalCommission = agents.reduce((s, a) => s + a.total_commission, 0);
  const pending = agents.reduce((s, a) => s + a.pending_commission, 0);

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-1.5">
            Manage agents
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Your sales team
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create agent accounts, set their per-agent commission and mark payouts as paid.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant">
          <UserPlus className="h-4 w-4" /> Add new agent
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <div className="rounded-2xl border border-border/70 bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Agents</div>
          <div className="mt-1 font-display text-2xl font-bold text-foreground">{agents.length}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{agents.filter((a) => a.active).length} active</div>
        </div>
        <div className="rounded-2xl border border-border/70 bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Total commission earned</div>
          <div className="mt-1 font-display text-2xl font-bold text-foreground">{fmtINR(totalCommission)}</div>
        </div>
        <div className="rounded-2xl border border-warning/30 bg-warning/5 p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Pending payouts</div>
          <div className="mt-1 font-display text-2xl font-bold text-foreground">{fmtINR(pending)}</div>
        </div>
      </div>

      <Card className="border-border/70 shadow-soft">
        <CardContent className="p-5 sm:p-6">
          <div className="relative max-w-md mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search agents"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-10 pl-9"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="py-14 text-center">
              <Users className="h-10 w-10 mx-auto text-primary/60 mb-2" />
              <div className="font-semibold text-foreground">No agents yet</div>
              <p className="text-sm text-muted-foreground mt-1">
                Add your first agent to start tracking commissions.
              </p>
              <Button onClick={openCreate} className="mt-4 bg-gradient-primary text-primary-foreground rounded-lg">
                <UserPlus className="h-4 w-4" /> Add agent
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-5 sm:-mx-6">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left font-semibold px-5 sm:px-6 py-3">Agent</th>
                    <th className="text-right font-semibold py-3">Rate</th>
                    <th className="text-right font-semibold py-3">Sold</th>
                    <th className="text-right font-semibold py-3">Revenue</th>
                    <th className="text-right font-semibold py-3">Commission</th>
                    <th className="text-center font-semibold py-3">Status</th>
                    <th className="text-right font-semibold px-5 sm:px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((a) => (
                    <tr key={a.agent_id} className="hover:bg-muted/40">
                      <td className="px-5 sm:px-6 py-3">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                            {a.agent_name.slice(0, 1).toUpperCase()}
                          </span>
                          <div>
                            <div className="font-semibold text-foreground">{a.agent_name}</div>
                            <div className="text-xs text-muted-foreground">@{a.agent_username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-right font-semibold text-foreground">{a.commission_rate}%</td>
                      <td className="py-3 text-right">{a.cards_sold}</td>
                      <td className="py-3 text-right">{fmtINR(a.total_revenue)}</td>
                      <td className="py-3 text-right">
                        <div className="font-semibold text-foreground">{fmtINR(a.total_commission)}</div>
                        <div className="text-[11px]">
                          <span className="text-success">{fmtINR(a.paid_commission)} paid</span>
                          {a.pending_commission > 0 && (
                            <span className="text-warning-foreground/80"> · {fmtINR(a.pending_commission)} pending</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-center">
                        <Switch checked={a.active} onCheckedChange={() => toggleActive(a)} />
                      </td>
                      <td className="px-5 sm:px-6 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {a.pending_commission > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-success border-success/30 hover:bg-success/10 hover:text-success"
                              onClick={() => setPayoutTarget(a)}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> Mark paid
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(a)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit agent" : "Add new agent"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update agent details and commission rate."
                : "Create a login for a sales agent. A one-time password will be shown after creation."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="f-name">Full name</Label>
                <Input id="f-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="f-user">Username</Label>
                <Input
                  id="f-user"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, "") })}
                  required
                  disabled={!!editing}
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="f-email">Email (optional)</Label>
                <Input
                  id="f-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="f-mobile">Mobile (optional)</Label>
                <Input
                  id="f-mobile"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-rate" className="flex items-center gap-1.5">
                <Percent className="h-3.5 w-3.5" /> Commission rate (%)
              </Label>
              <Input
                id="f-rate"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={form.commission_rate}
                onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">
                Agent earns ₹{Number(form.commission_rate || 0) || 0} on every ₹100 sale.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-pass">
                {editing ? "New password (optional)" : "Initial password (auto-generated if blank)"}
              </Label>
              <Input
                id="f-pass"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Leave blank to keep current" : "Leave blank to auto-generate"}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-primary text-primary-foreground rounded-xl"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
                {editing ? "Save changes" : "Create agent"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Initial password reveal dialog */}
      <Dialog open={!!createdPassword} onOpenChange={() => setCreatedPassword(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-success" /> Agent created
            </DialogTitle>
            <DialogDescription>
              Share these credentials securely with the agent. The password is shown only once.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Username</div>
              <div className="font-mono text-sm font-semibold text-foreground">{createdPassword?.username}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Password</div>
              <div className="flex items-center justify-between gap-3 mt-0.5">
                <div className="font-mono text-base font-semibold text-foreground tracking-wider">
                  {createdPassword?.password}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `Username: ${createdPassword?.username}\nPassword: ${createdPassword?.password}`
                    );
                    toast.success("Credentials copied");
                  }}
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedPassword(null)} className="bg-gradient-primary text-primary-foreground rounded-xl">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this agent?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.agent_name} (@{deleteTarget?.agent_username}) will no longer be able to
              log in. Their past sales remain in your records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove agent
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark paid confirm */}
      <AlertDialog open={!!payoutTarget} onOpenChange={() => setPayoutTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark commissions as paid?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {fmtINR(payoutTarget?.pending_commission)} pending commission for{" "}
              <span className="font-semibold text-foreground">{payoutTarget?.agent_name}</span> as paid out.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={markPaid} className="bg-success text-success-foreground hover:bg-success/90">
              Yes, mark paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
